(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sectionSelector = '#MainContent > .shopify-section';
  const explicitRootSelector = '[data-rd-native-motion]';
  const headingSelector = '[data-rd-native-heading], h1, h2, h3';
  const fadeSelector = [
    '[data-rd-native-lead]',
    '[data-rd-native-follow]',
    '[data-rd-native-media]',
    '.banner__content',
    '.slideshow__text-wrapper',
    '.image-with-text__img-wrapper',
    '.image-with-text__content-heading',
    '.image-with-text__item',
    '.rich-text__blocks > *',
    '.multicolumn-editorial__content > *',
    '.multicolumn-editorial__stat',
    '.rd-benefit-icons__header > *',
    '.rd-benefit-icons__item',
    '.collapsible-content__item',
    '.featured-collection__header > *',
    '.collection-list__header > *',
    '.blog__header > *',
    '.main-page-title',
    '.shopify-policy__body > *'
  ].join(',');
  const excludedSectionSelector = [
    '.main-product-section',
    '.product-section',
    '.main-cart-section',
    '.customer-section',
    '.predictive-search-section',
    '.rd-fit-quiz-section'
  ].join(',');
  const excludedAncestorSelector = [
    'nav',
    'form',
    'button',
    '[role="button"]',
    '[role="dialog"]',
    '[data-enable-header-animation]',
    '[data-rd-native-skip]',
    '.product-form',
    '.product-card',
    '.card-wrapper',
    '.cart-items',
    '.shopify-payment-button',
    '.rd-fit-quiz',
    '[data-rd-fit-quiz]'
  ].join(',');

  let observer;
  let scrollFrame;

  const isExcluded = (element) => Boolean(element.closest(excludedAncestorSelector));

  const wrapWords = (heading) => {
    if (!heading || heading.querySelector('.word') || isExcluded(heading)) return;

    let wordIndex = 0;
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (node.parentElement?.closest('button, [role="button"]')) return NodeFilter.FILTER_REJECT;
        return node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const parts = node.textContent.split(/(\s+)/);
      const fragment = document.createDocumentFragment();
      parts.forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }
        const span = document.createElement('span');
        span.className = 'word';
        span.style.setProperty('--word-index', wordIndex);
        span.textContent = part;
        fragment.appendChild(span);
        wordIndex += 1;
      });
      node.replaceWith(fragment);
    });
  };

  const activate = (section) => {
    section.classList.add('anim-active');
    observer?.unobserve(section);
  };

  const getObserver = () => {
    if (observer || reduceMotion.matches || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) activate(entry.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -25% 0px' });
    return observer;
  };

  const prepareRoot = (root, section) => {
    if (!root || root.dataset.rdNativePrepared === 'true' || isExcluded(root)) return false;

    const headings = Array.from(root.querySelectorAll(headingSelector)).filter((heading) => !isExcluded(heading));
    headings.forEach(wrapWords);

    const wordCount = root.querySelectorAll('.word').length;
    const hasLead = Boolean(root.querySelector('[data-rd-native-lead]'));
    root.style.setProperty('--heading-delay', hasLead ? '.1s' : '0s');

    const fades = Array.from(root.querySelectorAll(fadeSelector)).filter((item, index, candidates) => {
      if (isExcluded(item) || item.closest('[data-rd-native-heading]')) return false;
      return !candidates.some((candidate) => candidate !== item && candidate.contains(item));
    });

    let followIndex = 0;
    fades.slice(0, 12).forEach((item) => {
      item.classList.add('rd-native-fade-item');
      let delay = .1;
      if (item.hasAttribute('data-rd-native-lead')) {
        delay = 0;
      } else if (item.hasAttribute('data-rd-native-media')) {
        delay = .1;
      } else {
        delay = (hasLead ? .1 : 0) + (wordCount * .1) + (followIndex * .1);
        followIndex += 1;
      }
      item.style.setProperty('--rd-native-delay', `${delay}s`);
    });

    if (!wordCount && !fades.length) return false;
    root.dataset.rdNativePrepared = 'true';
    root.setAttribute('data-rd-native-ready', '');
    section.dataset.rdNativeSection = 'true';
    return true;
  };

  const prepareSection = (section) => {
    if (!section || section.matches(excludedSectionSelector) || section.querySelector('[data-enable-header-animation]')) return;

    const explicitRoots = Array.from(section.querySelectorAll(explicitRootSelector));
    const roots = explicitRoots.length ? explicitRoots : [section];
    const prepared = roots.some((root) => prepareRoot(root, section));
    if (!prepared) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      activate(section);
    } else {
      getObserver().observe(section);
    }
  };

  const revealReachedSections = () => {
    scrollFrame = undefined;
    document.querySelectorAll('[data-rd-native-section]:not(.anim-active)').forEach((section) => {
      const bounds = section.getBoundingClientRect();
      if (bounds.top <= window.innerHeight * .92 || bounds.bottom <= 0) activate(section);
    });
  };

  const queueReachedSectionsCheck = () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(revealReachedSections);
  };

  const initialize = (root = document) => {
    if (reduceMotion.matches) {
      root.querySelectorAll?.('[data-enable-header-animation]').forEach((motionRoot) => {
        motionRoot.closest('.shopify-section')?.classList.add('anim-active');
      });
    }
    if (root.matches?.(sectionSelector)) prepareSection(root);
    root.querySelectorAll?.(sectionSelector).forEach(prepareSection);
    queueReachedSectionsCheck();
  };

  reduceMotion.addEventListener?.('change', (event) => {
    if (event.matches) {
      document.querySelectorAll('[data-rd-native-section]').forEach(activate);
      document.querySelectorAll('[data-enable-header-animation]').forEach((motionRoot) => {
        motionRoot.closest('.shopify-section')?.classList.add('anim-active');
      });
    }
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:select', (event) => activate(event.target));
  window.addEventListener('scroll', queueReachedSectionsCheck, { passive: true });
  window.addEventListener('resize', queueReachedSectionsCheck);

  initialize();
})();
